import { normalizeChartTimeframe } from "../core/chart-timeframes.js";

const units = [
  { name: "minute", unit: "m" }, { name: "hour", unit: "h" },
  { name: "day", unit: "D" }, { name: "week", unit: "W" }, { name: "month", unit: "M" }
];

export function completeIntervalInput(value) {
  const match = /^(\d+)\s*([a-z]+)$/i.exec(value.trim());
  if (!match) return null;
  const prefix = match[2].toLowerCase();
  const resolved = match[2] === 'M' ? units.find(item => item.unit === 'M')
    : units.find(item => item.name.startsWith(prefix) || `${item.name}s` === prefix);
  if (!resolved) return null;
  const word = `${resolved.name}${Number(match[1]) === 1 ? "" : "s"}`;
  return { value: `${match[1]} ${word}`, selectionStart: match[1].length + 1 + prefix.length };
}

export function parseCustomInterval(value) {
  const canonical = normalizeChartTimeframe(value);
  if (canonical) return canonical;
  const match = /^(\d+)\s*([a-z]+)$/i.exec(value.trim());
  if (!match) return null;
  const prefix = match[2].toLowerCase();
  const resolved = units.find(item => item.name.startsWith(prefix) || `${item.name}s` === prefix);
  return resolved ? normalizeChartTimeframe(`${Number(match[1])}${resolved.unit}`) : null;
}

export class CustomIntervalMenu {
  constructor({ interval, menu, toggle, onOpen, onSubmit }) {
    this.toggle = toggle;
    this.onOpen = onOpen;
    this.onSubmit = onSubmit;
    this.button = document.createElement("button");
    this.button.type = "button";
    this.button.textContent = "Custom";
    this.button.setAttribute("role", "menuitem");
    this.button.setAttribute("aria-haspopup", "dialog");
    this.button.dataset.windowTimeframeCustom = "";
    menu.append(this.button);
    this.element = document.createElement("div");
    this.element.className = "chart-custom-interval";
    this.element.hidden = true;
    this.element.setAttribute("role", "dialog");
    this.element.setAttribute("aria-label", "Custom chart interval");
    this.element.innerHTML = `<label><span>Custom interval</span>
      <input type="text" autocomplete="off" spellcheck="false" aria-label="Custom interval" placeholder="e.g. 2 hours"></label>
      <p class="chart-custom-interval-hint">Number + minute, hour, day, week, or month</p>
      <p class="chart-custom-interval-error" role="alert" hidden></p>
      <button type="button">OK</button>`;
    interval.append(this.element);
    this.controller = new AbortController();
    document.addEventListener('pointerdown', event => {
      if (!interval.contains(event.target)) this.close();
    }, { capture: true, signal: this.controller.signal });
    this.input = this.element.querySelector("input");
    this.error = this.element.querySelector('[role="alert"]');
    this.button.addEventListener("click", () => this.show());
    this.element.querySelector("button").addEventListener("click", () => this.submit());
    this.input.addEventListener("input", event => {
      this.error.hidden = true;
      this.input.removeAttribute("aria-invalid");
      if (event.isComposing || event.inputType?.startsWith("delete") || this.input.selectionStart !== this.input.value.length) return;
      const completion = completeIntervalInput(this.input.value);
      if (!completion) return;
      this.input.value = completion.value;
      this.input.setSelectionRange(completion.selectionStart, completion.value.length);
    });
    this.element.addEventListener("keydown", event => {
      event.stopPropagation();
      if (event.key === "Enter" && !event.isComposing) { event.preventDefault(); this.submit(); }
      if (event.key === "Escape") { event.preventDefault(); this.close(true); }
    });
  }

  show() {
    this.onOpen();
    this.input.value = "";
    this.error.hidden = true;
    this.input.removeAttribute("aria-invalid");
    this.element.hidden = false;
    this.toggle.setAttribute("aria-expanded", "true");
    this.input.focus();
  }

  submit() {
    const value = parseCustomInterval(this.input.value);
    if (!value) {
      this.error.textContent = "Use a whole number: 1–1440 minutes, 1–24 hours, 1–365 days, 1–52 weeks, or 1–12 months.";
      this.error.hidden = false;
      this.input.setAttribute("aria-invalid", "true");
      this.input.focus();
      return;
    }
    this.onSubmit(value);
    this.close(true);
  }

  close(restoreFocus = false) {
    if (this.element.hidden) return;
    this.element.hidden = true;
    this.toggle.setAttribute("aria-expanded", "false");
    if (restoreFocus) this.toggle.focus();
  }

  destroy() {
    this.close();
    this.controller.abort();
    this.button.remove();
    this.element.remove();
  }
}
